import { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Avatar,
  Badge,
  Button,
  Input,
  Textarea,
  Alert,
  AlertDescription,
} from '@klaro7/ui';

import { DateOfBirthInput } from '@/components/DateOfBirthInput';
import { PasswordInput } from '@/components/PasswordInput';
import { resolveUploadUrl } from '@/lib/uploads';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useUpdateProfile, useChangePassword, useUploadAvatar } from '@/features/profile/hooks/useProfile';
import { PhoneChangeCard } from '@/features/phone-verification/components/PhoneChangeCard';
import { updateConsents } from '@/features/auth/services/auth.service';

export function UserProfilePage() {
  const { user, refreshUser } = useAuth();
  const updateProfileMutation = useUpdateProfile();
  const changePasswordMutation = useChangePassword();
  const uploadAvatarMutation = useUploadAvatar();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [avatarError, setAvatarError] = useState('');
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState<string | null>(null);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAvatarError('');
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setAvatarError('Il file è troppo grande. Il limite è 2 MB.');
      e.target.value = '';
      return;
    }

    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setAvatarError('Formato non supportato. Usa JPG, PNG, GIF o WebP.');
      e.target.value = '';
      return;
    }

    // Immediate optimistic preview while the upload is in flight.
    const objectUrl = URL.createObjectURL(file);
    setAvatarPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return objectUrl;
    });

    try {
      await uploadAvatarMutation.mutateAsync(file);
      // Pull the new avatar path into the auth user so the canonical URL
      // replaces the blob preview everywhere it is read from `user.profile`.
      await refreshUser();
    } catch (err) {
      setAvatarError(err instanceof Error ? err.message : 'Caricamento non riuscito');
      setAvatarPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }

    e.target.value = '';
  };

  // Clear the blob preview once the server-confirmed avatar is loaded.
  useEffect(() => {
    if (!avatarPreviewUrl) return;
    return () => {
      URL.revokeObjectURL(avatarPreviewUrl);
    };
  }, [avatarPreviewUrl]);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nickname, setNickname] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [bio, setBio] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const [consentError, setConsentError] = useState('');
  const [savingConsent, setSavingConsent] = useState<'marketing' | 'profiling' | null>(null);

  useEffect(() => {
    if (user?.profile) {
      setFirstName(user.profile.firstName || '');
      setLastName(user.profile.lastName || '');
      setNickname(user.profile.nickname || '');
      setDateOfBirth(
        user.profile.dateOfBirth
          ? new Date(user.profile.dateOfBirth).toISOString().split('T')[0] ?? ''
          : ''
      );
      setBio(user.profile.bio || '');
    }
  }, [user]);

  if (!user) return null;

  const fullName = `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim();

  const consents = user.consents;
  const formatConsentDate = (iso?: string) =>
    iso
      ? new Date(iso).toLocaleDateString('it-IT', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';

  const handleToggleConsent = async (key: 'marketing' | 'profiling', granted: boolean) => {
    setConsentError('');
    setSavingConsent(key);
    try {
      await updateConsents({ [key]: granted });
      await refreshUser();
    } catch (e) {
      setConsentError(e instanceof Error ? e.message : 'Errore durante l’aggiornamento dei consensi');
    } finally {
      setSavingConsent(null);
    }
  };

  const handleSaveProfile = async () => {
    setProfileError('');
    setProfileSuccess('');
    try {
      await updateProfileMutation.mutateAsync({
        firstName,
        lastName,
        // Send the trimmed value (possibly empty) — NOT `|| undefined`, or a
        // cleared field would be dropped by JSON.stringify and never cleared.
        nickname: nickname.trim(),
        dateOfBirth: dateOfBirth || undefined,
        bio: bio || undefined,
      });
      // Pull the persisted profile back into the auth context so the change
      // reflects immediately everywhere it reads from `user.profile`.
      await refreshUser();
      setProfileSuccess('Profilo aggiornato con successo!');
      setTimeout(() => setProfileSuccess(''), 5000);
    } catch (e) {
      setProfileError(e instanceof Error ? e.message : 'Errore durante il salvataggio');
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Le password non coincidono');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('La nuova password deve avere almeno 8 caratteri');
      return;
    }

    try {
      await changePasswordMutation.mutateAsync({
        currentPassword,
        newPassword,
      });
      setPasswordSuccess('Password aggiornata con successo!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(''), 5000);
    } catch (e) {
      setPasswordError(e instanceof Error ? e.message : 'Errore durante il cambio password');
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Profilo</h1>
          <p className="text-neutral-500">Gestisci le informazioni del tuo account</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* User Card */}
          <Card className="lg:col-span-1">
            <CardContent className="flex flex-col items-center pt-6">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadAvatarMutation.isPending}
                className="group relative rounded-full focus:outline-none focus:ring-2 focus:ring-brand-teal focus:ring-offset-2 disabled:opacity-60"
                title="Cambia foto profilo"
              >
                <Avatar
                  src={avatarPreviewUrl ?? resolveUploadUrl(user.profile?.avatar)}
                  fallback={fullName || user.email}
                  size="xl"
                />
                <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                  {uploadAvatarMutation.isPending ? '...' : 'Cambia'}
                </span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleAvatarChange}
                className="hidden"
              />
              {avatarError && <p className="mt-2 text-xs text-red-600">{avatarError}</p>}
              <h2 className="mt-4 text-lg font-semibold text-neutral-900">{fullName || 'Utente'}</h2>
              <p className="text-sm text-neutral-500">{user.email}</p>
              <Badge variant="primary" className="mt-2 capitalize">
                {user.role.replace('_', ' ')}
              </Badge>
              {user.profile?.dateOfBirth && (
                <div className="mt-4 flex items-center gap-2 text-sm text-neutral-500">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                  </svg>
                  <span>
                    {new Date(user.profile.dateOfBirth).toLocaleDateString('it-IT', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Personal Data Form */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Dati Personali</CardTitle>
              <CardDescription>Modifica le informazioni del tuo profilo</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {profileSuccess && (
                <Alert variant="success">
                  <AlertDescription>{profileSuccess}</AlertDescription>
                </Alert>
              )}
              {profileError && (
                <Alert variant="error">
                  <AlertDescription>{profileError}</AlertDescription>
                </Alert>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="Nome"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Il tuo nome"
                />
                <Input
                  label="Cognome"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Il tuo cognome"
                />
              </div>

              <Input
                label="Nickname (opzionale)"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="Come vuoi apparire al professionista in chiamata"
                helperText="Se impostato, il professionista vedrà questo al posto di nome e cognome quando lo chiami."
                maxLength={50}
              />

              <Input
                label="Email"
                value={user.email}
                disabled
                helperText="L'email non puo' essere modificata"
              />

              <PhoneChangeCard
                phone={user.profile?.phone}
                phoneVerified={user.profile?.phoneVerified}
                pendingPhone={user.profile?.pendingPhone}
                onChanged={() => { void refreshUser(); }}
              />

              <DateOfBirthInput
                label="Data di Nascita"
                value={dateOfBirth}
                onChange={setDateOfBirth}
                helperText="La data di nascita e' importante per la tua esperienza personalizzata"
              />

              <Textarea
                label="Bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Scrivi qualcosa su di te..."
                rows={3}
              />

              <div className="flex justify-end pt-2">
                <Button
                  variant="primary"
                  onClick={handleSaveProfile}
                  loading={updateProfileMutation.isPending}
                >
                  Salva Modifiche
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Password Change */}
        <Card>
          <CardHeader>
            <CardTitle>Cambia Password</CardTitle>
            <CardDescription>Aggiorna la password del tuo account</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {passwordSuccess && (
              <Alert variant="success">
                <AlertDescription>{passwordSuccess}</AlertDescription>
              </Alert>
            )}
            {passwordError && (
              <Alert variant="error">
                <AlertDescription>{passwordError}</AlertDescription>
              </Alert>
            )}

            <div className="grid gap-4 sm:grid-cols-3">
              <PasswordInput
                label="Password Attuale"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Inserisci la password attuale"
              />
              <PasswordInput
                label="Nuova Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Almeno 8 caratteri"
              />
              <PasswordInput
                label="Conferma Nuova Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Ripeti la nuova password"
              />
            </div>

            <div className="flex justify-end pt-2">
              <Button
                variant="primary"
                onClick={handleChangePassword}
                disabled={!currentPassword || !newPassword || !confirmPassword}
                loading={changePasswordMutation.isPending}
              >
                Aggiorna Password
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Consents & Privacy */}
        <Card>
          <CardHeader>
            <CardTitle>Consensi e privacy</CardTitle>
            <CardDescription>Gestisci i consensi forniti e le preferenze revocabili</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {consentError && (
              <Alert variant="error">
                <AlertDescription>{consentError}</AlertDescription>
              </Alert>
            )}

            {/* Read-only acceptance timestamps */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                <p className="text-sm font-medium text-neutral-900">Termini e Condizioni</p>
                <p className="mt-1 text-sm text-neutral-500">
                  Accettati il {formatConsentDate(consents?.termsAcceptedAt)}
                </p>
              </div>
              <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                <p className="text-sm font-medium text-neutral-900">Informativa Privacy</p>
                <p className="mt-1 text-sm text-neutral-500">
                  Accettata il {formatConsentDate(consents?.privacyAcceptedAt)}
                </p>
              </div>
            </div>

            {/* Revocable toggles */}
            <div className="space-y-4">
              <label className="flex items-center justify-between gap-4">
                <span className="text-sm font-medium text-neutral-700">
                  Comunicazioni marketing
                  <span className="block text-xs font-normal text-neutral-500">
                    Ricevi novità, offerte e aggiornamenti via email.
                  </span>
                </span>
                <div className="relative shrink-0">
                  <input
                    type="checkbox"
                    checked={consents?.marketing?.granted ?? false}
                    disabled={savingConsent !== null}
                    onChange={(e) => handleToggleConsent('marketing', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="h-6 w-11 rounded-full bg-neutral-200 peer-checked:bg-brand-teal transition-colors peer-disabled:opacity-50" />
                  <div className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
                </div>
              </label>

              <label className="flex items-center justify-between gap-4">
                <span className="text-sm font-medium text-neutral-700">
                  Profilazione
                  <span className="block text-xs font-normal text-neutral-500">
                    Consenti l’analisi delle preferenze per personalizzare l’esperienza.
                  </span>
                </span>
                <div className="relative shrink-0">
                  <input
                    type="checkbox"
                    checked={consents?.profiling?.granted ?? false}
                    disabled={savingConsent !== null}
                    onChange={(e) => handleToggleConsent('profiling', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="h-6 w-11 rounded-full bg-neutral-200 peer-checked:bg-brand-teal transition-colors peer-disabled:opacity-50" />
                  <div className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
                </div>
              </label>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
